import { useState, useEffect } from "react";
import { getFile, getFileId, getFileMetadata } from "../services/fileService";
import { Profile } from "../types/profile";
import { getProfile, getProfiles, getProfilesByIds } from "../services/profileService";
import { useStation } from "../contexts/StationContext";

type UseProfile = {
    profile: Profile;
    profiles: Profile[];
    isLoading: boolean;
    error: Error | null;
    fetchProfiles: (stationId: string) => void;
    fetchProfile: (profileId: string) => void;
    fetchProfilesByIds: (profileIds: string[]) => void;
}

export const useProfile = (profileId?: string, profileIds?: string[]): UseProfile => {
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [profile, setProfile] = useState<Profile>({} as Profile);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { station } = useStation();

    useEffect(() => {
        if (station?.id && !profileId && profileIds === undefined) fetchProfiles(station.id);
    }, [station?.id]);

    useEffect(() => {
        if (profileId) fetchProfile(profileId);
    }, [profileId]);

    useEffect(() => {
        if (profileIds?.length) fetchProfilesByIds(profileIds);
    }, [profileIds]);

    const fetchProfiles = async (stationId: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await getProfiles(stationId);
            const profilesWithImages = await Promise.all(
                response.data.map(async (profile) => {
                    if (!profile.profileImageUrl) return { ...profile, image: '', imageAlt: '' };

                    const imageUrl = getFile(profile.profileImageUrl);
                    const fileId = getFileId(profile.profileImageUrl);

                    try {
                        const fileMetadata = await getFileMetadata(fileId);
                        return {
                            ...profile,
                            image: imageUrl,
                            imageAlt: fileMetadata.altName || profile.name
                        };
                    } catch (err) {
                        console.log('Error fetching file metadata for profile', err);
                        return { ...profile, image: imageUrl, imageAlt: profile.name };
                    }
                })
            );

            setProfiles(profilesWithImages);
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to fetch profiles');
            console.log('Error fetching profiles', error);
            setError(error);
        } finally {
            setIsLoading(false);
        }
    }

    const fetchProfile = async (profileId: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await getProfile(profileId);

            let profileData = { ...response.data };

            // Get alt text for profile image
            if (profileData.profileImageUrl) {
                const imageUrl = getFile(profileData.profileImageUrl);
                const fileId = getFileId(profileData.profileImageUrl);

                try {
                    const fileMetadata = await getFileMetadata(fileId);
                    profileData = {
                        ...profileData,
                        image: imageUrl,
                        imageAlt: fileMetadata.altName || profileData.name
                    };
                } catch (err) {
                    console.log('Error fetching file metadata for profile', err);
                    profileData = {
                        ...profileData,
                        image: imageUrl,
                        imageAlt: profileData.name
                    };
                }
            }

            // Process gallery images
            if (profileData.gallery && profileData.gallery.length > 0) {
                const galleryWithAlt = await Promise.all(
                    profileData.gallery.filter(gallery => gallery.url).map(async (item) => {
                        const mediaUrl = getFile(item.url);
                        const fileId = getFileId(item.url);

                        try {
                            const fileMetadata = await getFileMetadata(fileId);
                            console.log('fileMetadata', fileMetadata);
                            return {
                                ...item,
                                mediaType: fileMetadata.mimeType.split('/')[0] === 'image' ? 'Image' : 'Video',
                                mediaUrl,
                                imageAlt: fileMetadata.altName || ''
                            };
                        } catch (err) {
                            console.log('Error fetching file metadata for gallery item', err);
                            return { ...item, mediaUrl, imageAlt: '' };
                        }
                    })
                );
                profileData.gallery = galleryWithAlt;
            }

            // Process videos
            if (profileData.videos && profileData.videos.length > 0) {
                profileData.videos = profileData.videos.map(video => ({
                    ...video,
                    videoUrl: getFile(video.url)
                }));
            }

            setProfile(profileData);
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to fetch profiles');
            console.log('Error fetching profiles', error);
            setError(error);
        } finally {
            setIsLoading(false);
        }
    }

    const fetchProfilesByIds = async (profileIds: string[]) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await getProfilesByIds(profileIds);
            const profilesWithImages = await Promise.all(
                response.data.map(async (profile) => {
                    if (!profile.profileImageUrl) return { ...profile, image: '', imageAlt: '' };

                    const imageUrl = getFile(profile.profileImageUrl);
                    const fileId = getFileId(profile.profileImageUrl);

                    try {
                        const fileMetadata = await getFileMetadata(fileId);
                        return {
                            ...profile,
                            image: imageUrl,
                            imageAlt: fileMetadata.altName || profile.name
                        };
                    } catch (err) {
                        console.log('Error fetching file metadata for profile', err);
                        return { ...profile, image: imageUrl, imageAlt: profile.name };
                    }
                })
            );

            setProfiles(profilesWithImages);
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to fetch profiles');
            console.log('Error fetching profiles', error);
            setError(error);
        } finally {
            setIsLoading(false);
        }
    }

    return { profile, profiles, isLoading, error, fetchProfiles, fetchProfile, fetchProfilesByIds };
}